import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Activity, ArrowLeft, Cpu, Loader2, RefreshCw, Timer, TrendingUp, Users,
  AlertTriangle, ShieldCheck, Sparkles, Zap,
} from "lucide-react";
import { toast } from "sonner";

function StatCard({ label, value, tone = "slate", icon: Icon }) {
  const toneMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900",
  };
  return (
    <div className={`rounded-2xl border p-5 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 opacity-70">
        {Icon && <Icon size={14} />}
        <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      </div>
      <p className="font-display text-3xl font-bold mt-2">{value}</p>
    </div>
  );
}

const KPI_MAP = [
  { key: "active_users_24h",       tone: "emerald", icon: Users },
  { key: "new_signups_7d",         tone: "indigo",  icon: TrendingUp },
  { key: "logins_24h",             tone: "slate",   icon: Activity },
  { key: "uploads_24h",            tone: "slate",   icon: Activity },
  { key: "notifs_sent_7d",         tone: "fuchsia", icon: Zap },
  { key: "digests_sent_7d",        tone: "fuchsia", icon: Zap },
  { key: "server_errors_24h",      tone: "rose",    icon: AlertTriangle },
  { key: "premium_conversion_rate",tone: "amber",   icon: Sparkles },
  { key: "security_incidents_7d",  tone: "rose",    icon: ShieldCheck },
];

export default function AdminObservability() {
  const { user, loading: authLoading } = useAuth();
  const [kpis, setKpis] = useState({});
  const [perf, setPerf] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState(24);

  const load = async () => {
    setLoading(true);
    try {
      const [k, p, a] = await Promise.all([
        api.get("/admin/observability/kpis"),
        api.get(`/admin/observability/perf?since_hours=${since}`),
        api.get("/admin/ai/status"),
      ]);
      setKpis(k.data);
      setPerf(p.data);
      setAi(a.data);
    } catch (err) {
      toast.error("Erreur chargement observability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, since]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-8" data-testid="admin-observability-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Retour Admin
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">
            Admin · Observabilité
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2 flex items-center gap-3">
            <Activity size={36} /> Observability
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Métriques temps réel de Student Hub. Utilisateurs actifs, performances API, erreurs, taux de conversion. Actualisé toutes les minutes.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="obs-refresh-btn">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </header>

      {/* KPI grid */}
      <section data-testid="obs-kpi-grid">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Indicateurs clés</h2>
        {loading && !Object.keys(kpis).length ? (
          <p className="text-sm text-slate-400 flex items-center gap-1">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {KPI_MAP.map(({ key, tone, icon }) => {
              const k = kpis[key];
              if (!k) return null;
              return (
                <StatCard
                  key={key}
                  label={k.label}
                  value={k.value ?? "—"}
                  tone={tone}
                  icon={icon}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* Performance */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="obs-perf-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
            <Timer size={20} /> Performances API
          </h2>
          <div className="flex gap-1.5">
            {[1, 6, 24, 168].map((h) => (
              <button
                key={h}
                onClick={() => setSince(h)}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  since === h ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`obs-perf-window-${h}h`}
              >
                {h === 168 ? "7j" : `${h}h`}
              </button>
            ))}
          </div>
        </div>

        {perf && perf.top_routes && perf.top_routes.length > 0 ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">Requêtes</p>
                <p className="font-semibold text-slate-900 text-lg">{perf.total_requests}</p>
              </div>
              <div className="rounded-lg bg-rose-50 p-3">
                <p className="text-[10px] uppercase text-rose-600 font-semibold">Erreurs 5xx</p>
                <p className="font-semibold text-rose-800 text-lg">{perf.total_errors}</p>
              </div>
              <div className="rounded-lg bg-amber-50 p-3">
                <p className="text-[10px] uppercase text-amber-700 font-semibold">Taux erreur</p>
                <p className="font-semibold text-amber-900 text-lg">{perf.global_error_rate}%</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                    <th className="py-2 pr-3 font-medium">Route</th>
                    <th className="py-2 pr-3 font-medium">Requêtes</th>
                    <th className="py-2 pr-3 font-medium">Latence moy.</th>
                    <th className="py-2 pr-3 font-medium">p95</th>
                    <th className="py-2 pr-3 font-medium">p99</th>
                    <th className="py-2 font-medium">Err.</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.top_routes.slice(0, 20).map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-mono text-xs">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] mr-1.5">{r.method}</span>
                        {r.route}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">{r.requests}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.avg_latency_ms?.toFixed(1)}ms</td>
                      <td className="py-2 pr-3 text-slate-500">{r.p95_latency_ms?.toFixed(1)}ms</td>
                      <td className="py-2 pr-3 text-slate-500">{r.p99_latency_ms?.toFixed(1)}ms</td>
                      <td className={`py-2 ${r.error_rate > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                        {r.error_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400 italic">
            Aucune donnée de perf sur la fenêtre sélectionnée. Les métriques sont flushées toutes les 60s.
          </p>
        )}
      </section>

      {/* AI provider */}
      {ai && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="obs-ai-section">
          <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2 mb-4">
            <Cpu size={20} /> IA · Provider
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Provider actif</p>
              <p className="font-mono font-semibold text-slate-900 mt-1">{ai.provider}</p>
            </div>
            <div className={`rounded-lg p-3 ${ai.enabled ? "bg-emerald-50" : "bg-slate-50"}`}>
              <p className={`text-[10px] uppercase font-semibold ${ai.enabled ? "text-emerald-700" : "text-slate-500"}`}>État</p>
              <p className={`font-semibold mt-1 ${ai.enabled ? "text-emerald-900" : "text-slate-500"}`}>
                {ai.enabled ? "🟢 Actif" : "⚪ Désactivé"}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Clé Emergent LLM</p>
              <p className={`font-semibold mt-1 ${ai.env_key_present ? "text-emerald-700" : "text-rose-700"}`}>
                {ai.env_key_present ? "présente" : "absente"}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Providers dispos</p>
              <p className="font-mono text-xs text-slate-700 mt-1">{ai.available.join(", ")}</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            L'IA est en <strong>skeleton</strong>. Pour l'activer : mettre <code className="bg-slate-100 px-1 rounded">AI_PROVIDER=claude</code> dans les variables d'environnement backend + redéployer.
            <br />Sans activation, tous les appels à <code className="bg-slate-100 px-1 rounded">summarize_digest()</code> retournent la version verbatim (aucun coût LLM).
          </p>
        </section>
      )}
    </div>
  );
}
